import qs from "query-string";

export type EventCategory =
    "build" | "device" | "file" | "gateway" | "group" | "license" | "product" | "provisioning" | "user" | "workspace";

export type EventEntityFilter = {
    category?: EventCategory;
    id?: string;
};

export const allEventCategories: readonly EventCategory[] = [
    "build",
    "device",
    "file",
    "gateway",
    "group",
    "license",
    "product",
    "provisioning",
    "user",
    "workspace",
];

export const allEventLevels: readonly API.EventLevel[] = ["notice", "warning"];

export const maxEventEntityFilters = 50;

// Tables and MUI pagination count pages from 0, the events API counts from 1.
export function toApiPageIndex(pageIndex?: number): number | undefined {
    return pageIndex === undefined ? undefined : pageIndex + 1;
}

function isEveryCategorySelected(categories?: EventCategory[]): boolean {
    return !!categories?.length && allEventCategories.every((category) => categories.includes(category));
}

// Selecting every category filters nothing, same as selecting none - drop it so it isn't multiplied into the id
// combinations below (all 10 categories x 50 ids would otherwise hit the 50-item cap for no reason).
export function getEffectiveEventCategories(categories?: EventCategory[]): EventCategory[] | undefined {
    return isEveryCategorySelected(categories) ? undefined : categories;
}

export function buildEventEntities(categories?: EventCategory[], entityIds?: string[]): EventEntityFilter[] | undefined {
    const effectiveCategories = getEffectiveEventCategories(categories);

    if (!effectiveCategories?.length && !entityIds?.length) {
        return undefined;
    }

    if (!entityIds?.length) {
        return effectiveCategories?.map((category) => ({ category }));
    }

    if (!effectiveCategories?.length) {
        return entityIds.map((id) => ({ id }));
    }

    return effectiveCategories.flatMap((category) => entityIds.map((id) => ({ category, id })));
}

function flattenEventEntities(entities: EventEntityFilter[]): Record<string, string> {
    const flattened: Record<string, string> = {};

    entities
        .filter((entity) => entity.category || entity.id)
        .forEach((entity, index) => {
            if (entity.category) {
                flattened[`entities[${index}].category`] = entity.category;
            }
            if (entity.id) {
                flattened[`entities[${index}].id`] = entity.id;
            }
        });

    return flattened;
}

export function stringifyEventParams(params: Record<string, unknown> & { entities?: EventEntityFilter[] }): string {
    const { entities, ...rest } = params;
    return qs.stringify({ ...rest, ...(entities ? flattenEventEntities(entities) : {}) }, { arrayFormat: "index" });
}
